import React, { createContext, Dispatch, useContext, useReducer } from 'react';
import type {
  WBudgetView,
  WBudgetItemView,
  WBudgetItemTypeView,
  WBudgetTaxonomyNodeView,
  WBudgetItemPaymentView,
} from '@zola/svc-web-api-ts-client';
import type { MappedWAccountVendorView } from 'api/vendorMarketplaceApi';
import type { AccountWeddingLocationView } from '@zola/svc-marketplace-ts-types';

import { getBudgetActions } from './actions';
import { BudgetActionTypes } from './actionTypes';
import { budgetAppReducer } from './reducers';

export type BudgetContextStateType = {
  busy: boolean;
  budget: {
    account_id?: number;
    balance_due_cents?: number;
    budgeted_cents?: number;
    cost_cents?: number;
    cost_cents_without_estimates?: number;
    paid_cents?: number;
    taxonomy_nodes?: WBudgetTaxonomyNodeView[];
    uuid?: string;
  };
  item_types?: WBudgetItemTypeView[];
  unsavedChanges: {
    unsavedMaxSpend?: number;
    unsavedItems: {
      [k: string]: WBudgetItemView;
    };
    unsavedTaxonomyNode?: WBudgetTaxonomyNodeView;
    unsavedOrderedTaxonomyNodes?: WBudgetTaxonomyNodeView[];
  };
  accountVendors?: MappedWAccountVendorView[];
  marketplaceLocation: AccountWeddingLocationView | Pick<AccountWeddingLocationView, 'citySlug'>;
};

export type BudgetContextActionsType = {
  receiveBudget: (budget: WBudgetView) => void;
  receiveBusyState: (busy: boolean) => void;
  receiveBudgetItem: (item: WBudgetItemView) => void;
  receiveBudgetItems: (items: WBudgetItemView[]) => void;
  receiveDeletedBudgetItem: (itemUuid: string) => void;
  receiveItemTypes: (itemTypes: WBudgetItemTypeView[]) => void;
  receiveTaxonomyNode: (taxonomyNode: WBudgetTaxonomyNodeView) => void;
  receiveTaxonomyNodes: (taxonomyNodes: WBudgetTaxonomyNodeView[]) => void;
  receiveDeletedTaxonomyNode: (taxonomyNodeUuid: string) => void;
  receiveUnsavedMaxSpend: (maxSpend: number) => void;
  receiveUnsavedItem: (itemUuid: string, fields: WBudgetItemView) => void;
  receiveUnsavedTaxonomyNode: (node: WBudgetTaxonomyNodeView) => void;
  receiveUnsavedOrderedTaxonomyNodes: (nodes: WBudgetTaxonomyNodeView[]) => void;
  clearUnsavedMaxSpend: () => void;
  clearUnsavedItem: (itemUuid: string) => void;
  clearUnsavedTaxonomyNode: () => void;
  clearUnsavedOrderedTaxonomyNodes: () => void;
  receiveBudgetPayment: (payment: WBudgetItemPaymentView) => void;
  receiveDeletedBudgetPayment: (paymentUuid: string) => void;
  receiveAccountVendors: (accountVendors: MappedWAccountVendorView[]) => void;
  receiveMarketplaceLocation: (marketplaceLocation: AccountWeddingLocationView) => void;
};

export type BudgetContextType = {
  state: BudgetContextStateType;
  dispatch: Dispatch<BudgetActionTypes>;
};

export const budgetInitialState: BudgetContextStateType = {
  busy: true,
  budget: {
    account_id: undefined,
    balance_due_cents: undefined,
    budgeted_cents: undefined,
    cost_cents: undefined,
    cost_cents_without_estimates: undefined,
    paid_cents: undefined,
    taxonomy_nodes: [],
    uuid: undefined,
  },
  item_types: [],
  unsavedChanges: {
    unsavedItems: {},
  },
  accountVendors: [],
  marketplaceLocation: { citySlug: 'new-york-ny' },
};

export const BudgetContext = createContext<BudgetContextType>({
  state: budgetInitialState,
  dispatch: () => null,
});

export const BudgetContextProvider: React.FC<{ initialState?: BudgetContextStateType }> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(budgetAppReducer, initialState || budgetInitialState);

  // TODO: state is being updated correctly on main fetch but seeing
  // 'TypeError: Cannot set properties of undefined (setting 'hook')' in console;
  // Chrome context extension isn't picking this up either...
  return <BudgetContext.Provider value={{ state, dispatch }}>{children}</BudgetContext.Provider>;
};

export const useBudgetContext = (): {
  state: BudgetContextStateType;
  actions: BudgetContextActionsType;
} => {
  const context = useContext(BudgetContext);
  if (context === undefined) {
    throw new Error('useBudgetContext must be used within a BudgetContextProvider');
  }
  const { state, dispatch } = context;
  const actions = getBudgetActions(dispatch);

  return { state, actions };
};
